import React, { useState, useEffect } from "react";
import { api } from "@/context/AuthContext";
import { Briefcase, Sparkles, Plus, Trash2, Pencil, RefreshCw, X, CheckCircle, AlertCircle } from "lucide-react";

const LINES = [
  { value: "commercial_property", label: "Commercial Property" },
  { value: "general_liability",   label: "General Liability" },
  { value: "workers_comp",        label: "Workers' Comp" },
  { value: "habitational",        label: "Habitational" },
  { value: "commercial_auto",     label: "Commercial Auto" },
];
const LINE_LABEL = Object.fromEntries(LINES.map(l => [l.value, l.label]));

const EMPTY = { name: "", description: "", inspection_types: ["commercial_property"], eligibility_criteria: [] };

function ProfileEditor({ initial, onSave, onCancel, saving }) {
  const [p, setP] = useState(initial);
  const toggleLine = (v) => setP(prev => ({
    ...prev,
    inspection_types: prev.inspection_types.includes(v)
      ? prev.inspection_types.filter(x => x !== v)
      : [...prev.inspection_types, v],
  }));
  const setCriterion = (i, key, val) => setP(prev => ({
    ...prev,
    eligibility_criteria: prev.eligibility_criteria.map((c, idx) => idx === i ? { ...c, [key]: val } : c),
  }));
  return (
    <div className="border border-navy/30 bg-navy/[0.02] rounded-xl p-5 space-y-4" data-testid="profile-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Profile Name *</label>
          <input data-testid="profile-name-input" value={p.name} onChange={e => setP({ ...p, name: e.target.value })}
            placeholder="e.g., Restaurants"
            className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Description</label>
          <input data-testid="profile-desc-input" value={p.description} onChange={e => setP({ ...p, description: e.target.value })}
            placeholder="Full-service restaurants with commercial kitchens"
            className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">Coverage Lines *</label>
        <div className="flex flex-wrap gap-2">
          {LINES.map(l => {
            const active = p.inspection_types.includes(l.value);
            return (
              <button key={l.value} type="button" data-testid={`profile-line-${l.value}`} onClick={() => toggleLine(l.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                {l.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-slate-600">Eligibility Requirements</label>
          <button type="button" data-testid="add-criterion-btn"
            onClick={() => setP(prev => ({ ...prev, eligibility_criteria: [...prev.eligibility_criteria, { text: "", severity: "required" }] }))}
            className="text-xs text-navy font-medium hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add requirement</button>
        </div>
        {p.eligibility_criteria.length === 0 && (
          <p className="text-xs text-slate-400">No requirements yet — the AI will assess each one during the survey and flag results for underwriters.</p>
        )}
        <div className="space-y-2">
          {p.eligibility_criteria.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input data-testid={`criterion-text-${i}`} value={c.text} onChange={e => setCriterion(i, "text", e.target.value)}
                placeholder="e.g., Automatic fire suppression system over all cooking surfaces"
                className="flex-1 h-9 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30" />
              <select value={c.severity} onChange={e => setCriterion(i, "severity", e.target.value)} data-testid={`criterion-severity-${i}`}
                className="h-9 text-xs border border-slate-200 rounded-lg px-2 bg-white">
                <option value="required">Required</option>
                <option value="preferred">Preferred</option>
              </select>
              <button type="button" onClick={() => setP(prev => ({ ...prev, eligibility_criteria: prev.eligibility_criteria.filter((_, idx) => idx !== i) }))}
                className="text-slate-300 hover:text-red-500" data-testid={`remove-criterion-${i}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button type="button" data-testid="save-profile-btn" disabled={saving || !p.name.trim() || p.inspection_types.length === 0}
          onClick={() => onSave(p)}
          className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save Profile"}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">Cancel</button>
      </div>
    </div>
  );
}

export default function BusinessProfilesTab() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | profile object
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState("");

  const load = () => api.get("/carrier/business-profiles").then(r => setProfiles(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleSave = async (p) => {
    setSaving(true); setError("");
    try {
      if (p.id) await api.put(`/carrier/business-profiles/${p.id}`, p);
      else await api.post("/carrier/business-profiles", p);
      setEditing(null);
      load();
    } catch (e) { setError(e.response?.data?.detail || "Failed to save profile"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await api.delete(`/carrier/business-profiles/${id}`);
    setProfiles(prev => prev.filter(p => p.id !== id));
  };

  const handleGenerate = async () => {
    setGenerating(true); setError(""); setDrafts([]);
    try {
      const res = await api.post("/carrier/business-profiles/extract", {}, { timeout: 120000 });
      setDrafts(res.data.profiles || []);
    } catch (e) { setError(e.response?.data?.detail || "Could not generate profiles from guidelines"); }
    setGenerating(false);
  };

  const saveDraft = async (draft, idx) => {
    try {
      await api.post("/carrier/business-profiles", { ...draft, source: "guidelines" });
      setDrafts(prev => prev.filter((_, i) => i !== idx));
      load();
    } catch (e) { setError(e.response?.data?.detail || "Failed to save profile"); }
  };

  if (loading) return <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-navy" /></div>;

  return (
    <div className="space-y-5" data-testid="business-profiles-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-800">Business Type Profiles</h2>
          <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
            Define the classes of business you write. Each profile auto-selects the right coverage lines when ordering
            a survey, and the AI assesses your eligibility requirements — flagging results for underwriters.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="generate-profiles-btn" onClick={handleGenerate} disabled={generating}
            className="flex items-center gap-2 px-3 py-2 border border-navy/30 text-navy rounded-lg text-sm font-medium hover:bg-navy/5 transition-colors disabled:opacity-50">
            <Sparkles className="w-4 h-4" /> {generating ? "AI is reading your guidelines…" : "Generate from Guidelines"}
          </button>
          <button data-testid="new-profile-btn" onClick={() => setEditing("new")}
            className="flex items-center gap-2 px-3 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors">
            <Plus className="w-4 h-4" /> New Profile
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="profiles-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {editing === "new" && <ProfileEditor initial={EMPTY} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />}

      {/* AI-generated drafts pending approval */}
      {drafts.length > 0 && (
        <div className="space-y-2" data-testid="profile-drafts">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">AI-suggested profiles — review & save</p>
          {drafts.map((d, i) => (
            <div key={i} className="border border-dashed border-navy/30 bg-navy/[0.02] rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`draft-profile-${i}`}>
              <div>
                <div className="font-medium text-slate-800 text-sm flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-navy" /> {d.name}
                </div>
                {d.description && <p className="text-xs text-slate-500 mt-0.5">{d.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {d.inspection_types.map(t => <span key={t} className="text-[11px] px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-slate-600">{LINE_LABEL[t]}</span>)}
                </div>
                {d.eligibility_criteria?.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {d.eligibility_criteria.map((c, j) => (
                      <li key={j} className="text-xs text-slate-600">
                        <span className={`font-semibold ${c.severity === "required" ? "text-red-600" : "text-amber-600"}`}>{c.severity === "required" ? "REQ" : "PREF"}</span> — {c.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button data-testid={`save-draft-${i}`} onClick={() => saveDraft(d, i)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-navy text-white rounded-lg text-xs font-medium hover:bg-navy-dark">
                  <CheckCircle className="w-3.5 h-3.5" /> Save
                </button>
                <button onClick={() => setDrafts(prev => prev.filter((_, idx) => idx !== i))}
                  className="text-xs text-slate-400 hover:text-slate-600">Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Saved profiles */}
      {profiles.length === 0 && drafts.length === 0 && editing !== "new" ? (
        <div className="py-10 text-center border border-dashed border-slate-200 rounded-xl" data-testid="no-profiles">
          <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No business type profiles yet</p>
          <p className="text-slate-400 text-sm mt-1">Create one manually, or let AI generate them from your uploaded underwriting guidelines.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {profiles.map(p => editing && editing.id === p.id ? (
            <ProfileEditor key={p.id} initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} saving={saving} />
          ) : (
            <div key={p.id} className="border border-slate-200 rounded-xl p-4 flex items-start justify-between gap-4" data-testid={`profile-card-${p.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-navy" />
                  <span className="font-medium text-slate-800">{p.name}</span>
                  {p.source === "guidelines" && <span className="text-[10px] px-1.5 py-0.5 bg-navy/10 text-navy rounded-full font-semibold">AI-GENERATED</span>}
                </div>
                {p.description && <p className="text-xs text-slate-500 mt-1">{p.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {p.inspection_types.map(t => <span key={t} className="text-[11px] px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-slate-600">{LINE_LABEL[t]}</span>)}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {p.eligibility_criteria?.length || 0} eligibility requirement{(p.eligibility_criteria?.length || 0) !== 1 ? "s" : ""}
                  {p.eligibility_criteria?.length > 0 && ` (${p.eligibility_criteria.filter(c => c.severity === "required").length} required)`}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button data-testid={`edit-profile-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-slate-400 hover:text-navy"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-profile-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
